class BtNewsletterPopup extends HTMLElement {
  static ANIMATION_MS = 480;

  constructor() {
    super();
    this.cookieName = `BeautyTools:newsletter-popup-${this.dataset.sectionId}`;
    this.submittedKey = `BeautyTools:newsletter-submitted-${this.dataset.sectionId}`;
    this.countdownStorageKey = `BeautyTools:newsletter-countdown-${this.dataset.sectionId}`;
    this.openTimer = null;
    this.copyFeedbackTimer = null;
    this.countdownTimer = null;
    this.boundOverlayClick = this.onOverlayClick.bind(this);
    this.boundCloseClick = this.onCloseClick.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundSectionSelect = this.onSectionSelect.bind(this);
    this.boundSectionLoad = this.onSectionLoad.bind(this);
    this.boundCopyCoupon = this.onCopyCoupon.bind(this);
    this.boundFormSubmit = this.onFormSubmit.bind(this);
    this.boundPhoneInput = this.onPhoneInput.bind(this);
    this.boundEntryTrigger = this.onEntryTrigger.bind(this);
  }

  connectedCallback() {
    if (document.body.classList.contains('template-password')) return;

    this.overlay = this.querySelector('[data-bt-nl-overlay]');
    this.closeButtons = this.querySelectorAll('[data-bt-nl-close]');
    this.form = this.querySelector('.bt-newsletter-popup__form');
    this.copyButton = this.querySelector('[data-bt-nl-copy-coupon]');
    this.couponCode = this.querySelector('[data-bt-nl-coupon-code]');
    this.copyFeedback = this.querySelector('[data-bt-nl-copy-feedback]');
    this.successPanel = this.querySelector('[data-bt-nl-success]');
    this.tagsInput = this.form?.querySelector('[data-bt-nl-tags]');
    this.phoneInput = this.form?.querySelector('[data-bt-nl-phone]');

    this.closeButtons.forEach((btn) => btn.addEventListener('click', this.boundCloseClick));
    this.copyButton?.addEventListener('click', this.boundCopyCoupon);
    this.form?.addEventListener('submit', this.boundFormSubmit);
    this.phoneInput?.addEventListener('input', this.boundPhoneInput);
    document.addEventListener('click', this.boundEntryTrigger);

    if (this.wasFormSubmitted()) {
      this.handleSuccessReturn();
      return;
    }

    if (!this.shouldInit()) return;

    if (this.dataset.designMode === 'true' && window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.boundSectionSelect);
      document.addEventListener('shopify:section:load', this.boundSectionLoad);
      return;
    }

    if (this.dataset.popupMode === 'waitlist') return;

    if (this.getCookie(this.cookieName)) return;

    const delay = Number(this.dataset.delay) || 0;
    this.openTimer = window.setTimeout(() => this.open(), delay * 1000);
  }

  disconnectedCallback() {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.copyFeedbackTimer);
    window.clearInterval(this.countdownTimer);
    this.closeButtons?.forEach((btn) => btn.removeEventListener('click', this.boundCloseClick));
    this.copyButton?.removeEventListener('click', this.boundCopyCoupon);
    this.form?.removeEventListener('submit', this.boundFormSubmit);
    this.phoneInput?.removeEventListener('input', this.boundPhoneInput);
    document.removeEventListener('click', this.boundEntryTrigger);
    document.removeEventListener('shopify:section:select', this.boundSectionSelect);
    document.removeEventListener('shopify:section:load', this.boundSectionLoad);
    document.removeEventListener('keydown', this.boundKeydown);
    this.overlay?.removeEventListener('click', this.boundOverlayClick);
  }

  static formatPhoneBr(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
    if (!digits.length) return '';

    if (digits.length <= 2) {
      return `(${digits}`;
    }

    if (digits.length <= 6) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }

    if (digits.length <= 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }

  onPhoneInput(event) {
    const input = event.currentTarget;
    input.value = BtNewsletterPopup.formatPhoneBr(input.value);
  }

  onFormSubmit(event) {
    if (this.phoneInput) {
      const formattedPhone = BtNewsletterPopup.formatPhoneBr(this.phoneInput.value);
      this.phoneInput.value = formattedPhone;

      if (!formattedPhone && this.phoneInput.required) {
        event.preventDefault();
        this.phoneInput.setCustomValidity('Informe um telefone válido.');
        this.phoneInput.reportValidity();
        return;
      }

      this.phoneInput.setCustomValidity('');
    }

    sessionStorage.setItem(this.submittedKey, '1');

    const tags =
      this.dataset.popupMode === 'waitlist'
        ? [(this.dataset.customerTag || 'lista-de-espera').trim()]
        : ['newsletter', 'Inscrição por pop-up de entrada'];

    if (this.phoneInput) {
      const formattedPhone = BtNewsletterPopup.formatPhoneBr(this.phoneInput.value);
      if (formattedPhone) tags.push(`whatsapp: ${formattedPhone}`);
    }

    if (this.tagsInput) {
      this.tagsInput.value = tags.join(', ');
    }
  }

  wasFormSubmitted() {
    if (this.form?.querySelector('.notification.warning')) {
      sessionStorage.removeItem(this.submittedKey);
      return false;
    }

    if (this.querySelector('[data-bt-nl-success-marker]')) return true;
    if (this.classList.contains('is-submitted')) return true;

    const params = new URLSearchParams(window.location.search);
    const urlSuccess =
      params.get('bt_nl_subscribed') === '1' ||
      params.get('customer_posted') === 'true' ||
      params.get('contact_posted') === 'true';
    const sessionSubmitted = sessionStorage.getItem(this.submittedKey) === '1';

    if (urlSuccess || sessionSubmitted) return true;

    return false;
  }

  handleSuccessReturn() {
    this.showSuccessState();
    this.cleanSuccessUrl();
    sessionStorage.removeItem(this.submittedKey);
    this.open();
    this.setDismissCookie();
  }

  cleanSuccessUrl() {
    const url = new URL(window.location.href);
    let changed = false;

    ['bt_nl_subscribed', 'customer_posted', 'contact_posted'].forEach((key) => {
      if (!url.searchParams.has(key)) return;
      url.searchParams.delete(key);
      changed = true;
    });

    if (!changed) return;

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }

  shouldInit() {
    if (this.dataset.enable !== 'true') return false;
    if (this.dataset.showOnMobile === 'false' && window.innerWidth < 768) {
      this.classList.add('bt-newsletter-popup--hide-mobile');
      return false;
    }
    if (this.dataset.pages === 'home' && !document.body.classList.contains('template-index')) {
      return false;
    }
    if (document.body.classList.contains('template-password')) return false;
    return true;
  }

  showSuccessState() {
    this.classList.add('is-submitted');
    this.successPanel?.removeAttribute('hidden');
    this.initCountdown();
  }

  getCountdownEndTime() {
    const type = this.dataset.countdownType;

    if (type === 'date' && this.dataset.countdownEnd) {
      const end = new Date(this.dataset.countdownEnd).getTime();
      return Number.isNaN(end) ? null : end;
    }

    if (type === 'duration') {
      const minutes = Number(this.dataset.countdownMinutes) || 15;
      const durationMs = minutes * 60 * 1000;
      const stored = sessionStorage.getItem(this.countdownStorageKey);

      if (stored) {
        const parsed = Number(stored);
        if (!Number.isNaN(parsed)) return parsed;
      }

      const endTime = Date.now() + durationMs;
      sessionStorage.setItem(this.countdownStorageKey, String(endTime));
      return endTime;
    }

    return null;
  }

  initCountdown() {
    if (this.dataset.countdownEnabled !== 'true') return;

    const countdownEl = this.querySelector('[data-bt-nl-countdown]');
    if (!countdownEl) return;

    const endTime = this.getCountdownEndTime();
    if (!endTime) return;

    const daysEl = this.querySelector('[data-countdown-days]');
    const hoursEl = this.querySelector('[data-countdown-hours]');
    const minutesEl = this.querySelector('[data-countdown-minutes]');
    const secondsEl = this.querySelector('[data-countdown-seconds]');
    const expiredEl = this.querySelector('[data-bt-nl-countdown-expired]');
    const gridEl = this.querySelector('[data-bt-nl-countdown-grid]');

    const pad = (value) => String(value).padStart(2, '0');

    const render = () => {
      const remaining = endTime - Date.now();

      if (remaining <= 0) {
        window.clearInterval(this.countdownTimer);
        this.classList.add('is-countdown-expired');
        gridEl?.classList.add('m:hidden');
        expiredEl?.classList.remove('m:hidden');
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (daysEl) daysEl.textContent = pad(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minutesEl) minutesEl.textContent = pad(minutes);
      if (secondsEl) secondsEl.textContent = pad(seconds);
    };

    render();
    this.countdownTimer = window.setInterval(render, 1000);
  }

  onCopyCoupon() {
    const code = this.couponCode?.textContent?.trim();
    if (!code) return;

    const showFeedback = () => {
      if (!this.copyFeedback) return;
      this.copyFeedback.classList.remove('m:hidden');
      window.clearTimeout(this.copyFeedbackTimer);
      this.copyFeedbackTimer = window.setTimeout(() => {
        this.copyFeedback.classList.add('m:hidden');
      }, 2500);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(showFeedback).catch(() => this.fallbackCopy(code, showFeedback));
      return;
    }

    this.fallbackCopy(code, showFeedback);
  }

  fallbackCopy(text, callback) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      callback();
    } catch (error) {
      console.warn('BtNewsletterPopup: copy failed', error);
    }
    document.body.removeChild(textarea);
  }

  onSectionSelect(event) {
    if (event.detail.sectionId === this.dataset.sectionId && this.dataset.designMode === 'true') {
      this.open();
    } else {
      this.close(false);
    }
  }

  onSectionLoad(event) {
    if (event.detail.sectionId === this.dataset.sectionId && this.dataset.designMode === 'true') {
      this.open();
    }
  }

  onEntryTrigger(event) {
    const trigger = event.target.closest('[data-bt-open-entry-popup]');
    if (!trigger) return;
    if (this.dataset.showOnMobile === 'false' && window.innerWidth < 768) return;
    if (this.dataset.pages === 'home' && !document.body.classList.contains('template-index')) return;
    event.preventDefault();
    this.open();
  }

  onOverlayClick(event) {
    if (this.dataset.closeOnOverlay !== 'true') return;
    if (event.target === this.overlay) this.dismiss();
  }

  onCloseClick(event) {
    event.preventDefault();
    this.dismiss();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.dismiss();
  }

  open() {
    this.removeAttribute('hidden');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-visible');
      });
    });
    document.documentElement.classList.add('prevent-scroll');
    document.addEventListener('keydown', this.boundKeydown);
    if (this.overlay) this.overlay.addEventListener('click', this.boundOverlayClick);
  }

  close(persist) {
    this.classList.remove('is-visible');
    document.documentElement.classList.remove('prevent-scroll');
    document.removeEventListener('keydown', this.boundKeydown);
    this.overlay?.removeEventListener('click', this.boundOverlayClick);
    window.clearInterval(this.countdownTimer);

    window.setTimeout(() => {
      this.setAttribute('hidden', 'hidden');
    }, BtNewsletterPopup.ANIMATION_MS);

    if (persist) this.setDismissCookie();
  }

  dismiss(persist = true) {
    this.close(persist);
  }

  setDismissCookie() {
    const days = Number(this.dataset.cookieDays) || 30;
    this.setCookie(this.cookieName, 'dismissed', days);
  }

  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }
}

if (!customElements.get('bt-newsletter-popup')) {
  customElements.define('bt-newsletter-popup', BtNewsletterPopup);
}
